
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import LoadingBuffer from "../components/LoadingBuffer";
import { handleUniversalAddToCart, getProductStock } from "../utils/cartHelper";
import ProductCartButton from "../components/ProductCartButton";

const API_BASE =
  "https://api.vanyacollectionsrajahmundry.com";

const PRODUCTS_API = `${API_BASE}/products/all`;
const CATEGORIES_API = `${API_BASE}/categories`;

const normalizeCatKey = (val = "") =>
  String(val || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");

const VanyaNewArrivals = () => {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingProductId, setAddingProductId] = useState(null);

  useEffect(() => {
    fetchNewArrivals();

    const handleSync = () => fetchNewArrivals();
    window.addEventListener("focus", handleSync);
    window.addEventListener("storage", handleSync);
    window.addEventListener("vanya_category_sync", handleSync);
    return () => {
      window.removeEventListener("focus", handleSync);
      window.removeEventListener("storage", handleSync);
      window.removeEventListener("vanya_category_sync", handleSync);
    };
  }, []);

  // ============================================================
  // FETCH NEW ARRIVALS
  // ============================================================

  const fetchNewArrivals = async () => {
    try {
      const [res, catRes] = await Promise.all([
        fetch(PRODUCTS_API),
        fetch(CATEGORIES_API).catch(() => null),
      ]);

      if (!res.ok) {
        throw new Error("Failed to fetch products");
      }

      const data = await res.json();
      const catData = catRes && catRes.ok ? await catRes.json() : [];
      const apiCategories = Array.isArray(catData) ? catData : [];

      const newArrivals = Array.isArray(data)
        ? data
            .filter(
              (p) =>
                !p.is_hold &&
                String(p.type || "")
                  .trim()
                  .toLowerCase() === "new arrival"
            )
            .map((p) => {
              const normCat = normalizeCatKey(p.category);
              const matchedCat = apiCategories.find(
                (c) => normalizeCatKey(c?.category) === normCat
              );
              return matchedCat?.category
                ? { ...p, category: matchedCat.category }
                : p;
            })
        : [];

      setProducts(newArrivals);
    } catch (err) {
      console.error(
        "Error fetching new arrivals:",
        err
      );

      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // QUICK VIEW
  // ============================================================

  const handleQuickView = (e, product) => {
    e.preventDefault();
    e.stopPropagation();

    if (!product?.id) {
      console.error("Product ID missing");
      return;
    }

    // Open product detail page
    navigate(`/product/${product.id}`);
  };

  // ============================================================
  // ADD TO CART
  // ============================================================

  const handleAddToCart = async (e, product) => {
    e.preventDefault();
    e.stopPropagation();

    if (!product?.id) {
      alert("Product ID is missing.");
      return;
    }

    if (addingProductId === product.id) {
      return;
    }

    try {
      setAddingProductId(product.id);

      const availableStock = getProductStock(product);
      if (availableStock <= 0) {
        alert(`"${product.name || 'This saree'}" is currently out of stock.`);
        return;
      }

      const result = await handleUniversalAddToCart(product);

      if (result.outOfStock) {
        alert(result.message || `"${product.name || 'This saree'}" is currently out of stock.`);
        return;
      }

      if (result.needsColourSelection) {
        alert("Please select a colour first.");
        navigate(`/product/${product.id}`);
        return;
      }

      if (result.success) {
        alert(result.message || `${product.name || "Product"} added to bag successfully!`);
      }
    } catch (error) {
      console.error("Add to cart error:", error);
      alert(error.message || "Unable to add product to cart.");
    } finally {
      setAddingProductId(null);
    }
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div
      style={styles.container}
      className="new-arrivals-container"
    >
      {/* ====================================================== */}
      {/* HEADER */}
      {/* ====================================================== */}

      <div
        style={styles.header}
        className="new-arrivals-header"
      >
        <div>
          <span style={styles.tagline}>
            ✦ CURATED EXCLUSIVES ✦
          </span>

          <h2 style={styles.title}>
            New Arrivals
          </h2>

          <div style={styles.underline}></div>
        </div>
      </div>

      {/* ====================================================== */}
      {/* LOADING */}
      {/* ====================================================== */}

      {loading && (
        <LoadingBuffer 
          message="Curating Fresh Royal Weaves..." 
          fullPage={true} 
        />
      )}

      {/* ====================================================== */}
      {/* EMPTY */}
      {/* ====================================================== */}

      {!loading && products.length === 0 && (
        <div style={styles.emptyMessage}>
          No new arrivals available.
        </div>
      )}

      {/* ====================================================== */}
      {/* PRODUCT GRID */}
      {/* ====================================================== */}

      <div
        style={styles.grid}
        className="new-arrivals-grid"
      >
        {products.map((product) => {
          const isAdding =
            addingProductId === product.id;
          const isOutOfStock = getProductStock(product) <= 0;

          return (
            <div
              key={product.id}
              className="product-card"
              style={styles.productCard}
            >
              {/* ================================================= */}
              {/* IMAGE AREA */}
              {/* ================================================= */}

              <div
                className="product-image-wrapper"
                style={styles.imageWrapper}
              >
                {/* IMAGE LINK */}
                <Link
                  to={`/product/${product.id}`}
                  style={styles.imageLink}
                  onClick={(e) => {
                    // Normal image click -> product page
                    e.stopPropagation();
                  }}
                >
                  <img
                    src={
                      product.img_url ||
                      product.thumbnails?.[0] ||
                      product.variants?.[0]?.mainImage ||
                      product.variants?.[0]?.thumbnails?.[0] ||
                      ""
                    }
                    alt={
                      product.name ||
                      "Product"
                    }
                    style={styles.image}
                    className="product-image"
                    loading="lazy"
                  />
                </Link>

                {/* ================================================= */}
                {/* BADGES */}
                {/* ================================================= */}

                <div
                  style={styles.badgeContainer}
                >
                  {isOutOfStock ? (
                    <span
                      style={{
                        backgroundColor: "#C22730",
                        color: "#FFFFFF",
                        fontSize: "8.5px",
                        padding: "4px 9px",
                        borderRadius: "999px",
                        fontWeight: "700",
                        letterSpacing: "1px",
                        boxShadow: "0 2px 6px rgba(194, 39, 48, 0.4)",
                      }}
                    >
                      OUT OF STOCK
                    </span>
                  ) : (
                    <span
                      style={styles.badgeNew}
                    >
                      NEW
                    </span>
                  )}

                  {Number(
                    product.discount || 0
                  ) > 0 && (
                    <span
                      style={
                        styles.badgeDiscount
                      }
                    >
                      {product.discount}% OFF
                    </span>
                  )}
                </div>

                {/* ================================================= */}
                {/* DESKTOP ACTIONS */}
                {/* ================================================= */}

                <div
                  className="desktop-actions"
                  style={styles.desktopActions}
                >
                  {/* QUICK VIEW */}
                  <button
                    type="button"
                    style={styles.quickView}
                    onClick={(e) =>
                      handleQuickView(
                        e,
                        product
                      )
                    }
                  >
                    Quick View
                  </button>

                  {/* ADD TO CART */}
                  <ProductCartButton product={product} />
                </div>
              </div>

              {/* ================================================= */}
              {/* PRODUCT INFORMATION */}
              {/* ================================================= */}

              <Link
                to={`/product/${product.id}`}
                style={
                  styles.productInfoLink
                }
              >
                <div
                  style={styles.info}
                  className="product-info"
                >
                  <p
                    style={styles.category}
                  >
                    {product.category ||
                      "Handloom Saree"}
                  </p>

                  <h3
                    style={
                      styles.productName
                    }
                    title={product.name}
                  >
                    {product.name}
                  </h3>

                  <div
                    style={styles.priceRow}
                  >
                    <span
                      style={
                        styles.currentPrice
                      }
                    >
                      ₹
                      {Number(
                        product.price || 0
                      ).toLocaleString(
                        "en-IN"
                      )}
                    </span>

                    {product.old_price &&
                      Number(
                        product.old_price
                      ) >
                        Number(
                          product.price
                        ) && (
                        <span
                          style={
                            styles.oldPrice
                          }
                        >
                          ₹
                          {Number(
                            product.old_price
                          ).toLocaleString(
                            "en-IN"
                          )}
                        </span>
                      )}
                  </div>
                </div>
              </Link>

              {/* ================================================= */}
              {/* MOBILE ACTIONS */}
              {/* ================================================= */}

              <div
                className="mobile-actions"
                style={styles.mobileActions}
              >
                {/* QUICK VIEW */}
                <button
                  type="button"
                  style={
                    styles.mobileQuickView
                  }
                  onClick={(e) =>
                    handleQuickView(
                      e,
                      product
                    )
                  }
                >
                  Quick View
                </button>

                {/* ADD TO BAG */}
                <ProductCartButton product={product} compact={true} />
              </div>
            </div>
          );
        })}
      </div>

      {/* ====================================================== */}
      {/* CSS */}
      {/* ====================================================== */}

      <style>
        {`
          .new-arrivals-container,
          .new-arrivals-container * {
            box-sizing: border-box;
          }

          /* =========================
             PRODUCT CARD
          ========================= */

          .product-card {
            border-radius: 14px;
            overflow: hidden;
            box-shadow: 0 8px 24px rgba(75, 41, 84, 0.06);
            border: 1px solid rgba(75, 41, 84, 0.08);
            background: #FFFFFF;
            transition:
              transform 0.35s cubic-bezier(
                0.165,
                0.84,
                0.44,
                1
              ),
              box-shadow 0.35s ease,
              border-color 0.35s ease;
          }

          @media (min-width: 769px) {

            .product-card:hover {
              transform: translateY(-5px);

              border-color: #D4AF37 !important;

              box-shadow:
                0 16px 36px rgba(
                  75,
                  41,
                  84,
                  0.12
                ),
                0 0 15px rgba(
                  212,
                  175,
                  55,
                  0.2
                ) !important;
            }

            .product-card:hover
            .product-image {
              transform: scale(1.05);
            }

            /* DESKTOP ACTIONS */

            .desktop-actions {
              opacity: 0;
              visibility: hidden;
              transform:
                translateY(15px);

              transition:
                opacity 0.25s ease,
                transform 0.25s ease,
                visibility 0.25s ease;
            }

            .product-image-wrapper:hover
            .desktop-actions {
              opacity: 1;
              visibility: visible;
              transform: translateY(0);
            }

            /* MOBILE ACTIONS HIDDEN */

            .mobile-actions {
              display: none !important;
            }
          }

          /* =========================
             IMAGE
          ========================= */

          .product-image {
            transition:
              transform 0.5s ease;
          }

          /* =========================
             BUTTONS
          ========================= */

          button {
            font-family:
              "Montserrat",
              sans-serif;
          }

          button:disabled {
            cursor: not-allowed !important;
          }

          .desktop-actions button,
          .mobile-actions button {
            -webkit-tap-highlight-color:
              transparent;

            touch-action: manipulation;
          }

          /* =========================
             MOBILE
          ========================= */

          @media (max-width: 768px) {

            .new-arrivals-container {
              padding: 20px 10px !important;
              width: 100%;
            }

            .new-arrivals-header {
              margin-bottom: 20px !important;
              padding-bottom: 12px !important;
            }

            .new-arrivals-grid {
              grid-template-columns:
                repeat(
                  2,
                  minmax(0, 1fr)
                ) !important;

              gap: 10px !important;
            }

            /* HIDE DESKTOP */

            .desktop-actions {
              display: none !important;
            }

            /* SHOW MOBILE */

            .mobile-actions {
              display: flex !important;

              width: 100%;

              padding:
                0 8px 10px;

              gap: 6px;

              position: relative;

              z-index: 10;
            }

            .mobile-actions button {
              min-height: 34px;

              padding:
                6px 4px;

              font-size: 10px !important;

              font-weight: 600;

              border-radius: 8px !important;

              cursor: pointer;

              letter-spacing: 0.3px;

              transition: transform 0.15s ease;
            }

            .mobile-actions button:active {
              transform: scale(0.96);
            }

            .product-image-wrapper {
              height: 190px !important;
            }

            .product-info {
              padding:
                10px 8px !important;
            }

            .product-card {
              border-radius: 14px !important;
            }

            .product-name {
              font-size: 12.5px !important;
            }

            .current-price {
              font-size: 13px !important;
            }

            .old-price {
              font-size: 10px !important;
            }
          }

          /* =========================
             SMALL MOBILE
          ========================= */

          @media (max-width: 480px) {

            .new-arrivals-container {
              padding:
                16px 8px !important;
            }

            .new-arrivals-grid {
              gap: 8px !important;
            }

            .product-image-wrapper {
              height: 165px !important;
            }

            .new-arrivals-header {
              margin-bottom: 16px !important;
            }

            .mobile-actions {
              padding:
                0 6px 8px !important;

              gap: 5px !important;
            }

            .mobile-actions button {
              font-size: 9.5px !important;

              min-height: 34px !important;

              padding:
                6px 3px !important;
            }
          }

          /* =========================
             VERY SMALL
          ========================= */

          @media (max-width: 360px) {

            .product-image-wrapper {
              height: 145px !important;
            }

            .new-arrivals-grid {
              gap: 6px !important;
            }

            .mobile-actions button {
              font-size: 9px !important;
            }
          }
        `}
      </style>
    </div>
  );
};

// ============================================================
// STYLES
// ============================================================

const styles = {
  container: {
    padding: "35px 20px 50px",
    maxWidth: "1300px",
    margin: "0 auto",
    fontFamily:
      '"Montserrat", sans-serif',
    backgroundColor: "#FAF5FC",
    width: "100%",
  },

  header: {
    display: "flex",
    justifyContent:
      "space-between",
    alignItems: "flex-end",
    marginBottom: "35px",
    borderBottom:
      "1px solid rgba(212, 175, 55, 0.25)",
    paddingBottom: "15px",
  },

  tagline: {
    color: "#D4AF37",
    fontSize: "10px",
    fontWeight: "600",
    letterSpacing: "4px",
  },

  title: {
    fontSize:
      "clamp(1.8rem, 3vw, 2.4rem)",
    margin: "6px 0 0",
    fontFamily:
      '"Playfair Display", serif',
    color: "#4B2954",
    fontWeight: "700",
  },

  underline: {
    width: "40px",
    height: "2px",
    backgroundColor: "#D4AF37",
    marginTop: "8px",
  },

  emptyMessage: {
    textAlign: "center",
    padding: "50px 20px",
    color: "#4B2954",
    fontSize: "14px",
  },

  // ==========================================================
  // GRID
  // ==========================================================

  grid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "24px",
    width: "100%",
  },

  // ==========================================================
  // CARD
  // ==========================================================

  productCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: "14px",
    overflow: "hidden",
    border:
      "1px solid rgba(75, 41, 84, 0.08)",
    boxShadow:
      "0 8px 24px rgba(75, 41, 84, 0.06)",
    position: "relative",
    minWidth: 0,
  },

  // ==========================================================
  // IMAGE
  // ==========================================================

  imageWrapper: {
    position: "relative",
    height: "320px",
    overflow: "hidden",
    backgroundColor: "#F3EBF5",
  },

  imageLink: {
    display: "block",
    width: "100%",
    height: "100%",
  },

  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },

  // ==========================================================
  // BADGES
  // ==========================================================

  badgeContainer: {
    position: "absolute",
    top: "10px",
    left: "10px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    zIndex: 3,
    pointerEvents: "none",
  },

  badgeNew: {
    backgroundColor: "#4B2954",
    color: "#FFFFFF",
    fontSize: "8.5px",
    padding: "4px 9px",
    borderRadius: "999px",
    fontWeight: "600",
    letterSpacing: "1px",
    boxShadow: "0 2px 6px rgba(75, 41, 84, 0.2)",
  },

  badgeDiscount: {
    backgroundColor: "#C22730",
    color: "#FFFFFF",
    fontSize: "8.5px",
    padding: "4px 9px",
    borderRadius: "999px",
    fontWeight: "600",
    letterSpacing: "1px",
    boxShadow: "0 2px 6px rgba(194, 39, 48, 0.2)",
  },

  // ==========================================================
  // DESKTOP ACTIONS
  // ==========================================================

  desktopActions: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,

    padding: "12px",

    display: "flex",
    gap: "8px",

    background:
      "linear-gradient(to top, rgba(75, 41, 84, 0.9) 0%, rgba(75, 41, 84, 0.45) 70%, transparent 100%)",

    backdropFilter:
      "blur(3px)",

    zIndex: 5,
  },

  quickView: {
    flex: 1,

    padding: "9px 8px",

    border: "1px solid rgba(75, 41, 84, 0.15)",

    borderRadius: "8px",

    backgroundColor:
      "rgba(255,255,255,0.98)",

    color: "#4B2954",

    cursor: "pointer",

    fontSize: "10px",

    fontWeight: "600",

    letterSpacing: "0.5px",

    textTransform: "uppercase",

    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
  },

  addToCart: {
    flex: 1,

    padding: "9px 8px",

    border: "none",

    borderRadius: "8px",

    background: "linear-gradient(135deg, #e2bc53 0%, #c99e32 100%)",

    color: "#222",

    cursor: "pointer",

    fontSize: "10px",

    fontWeight: "700",

    letterSpacing: "0.5px",

    textTransform: "uppercase",

    boxShadow: "0 3px 10px rgba(212, 175, 55, 0.35)",
  },

  // ==========================================================
  // PRODUCT INFO
  // ==========================================================

  productInfoLink: {
    textDecoration: "none",
    color: "inherit",
    display: "block",
  },

  info: {
    padding: "16px 14px",
  },

  category: {
    color: "#8E7394",

    fontSize: "9px",

    margin: "0 0 5px",

    letterSpacing: "1.2px",

    textTransform: "uppercase",

    fontWeight: "500",

    whiteSpace: "nowrap",

    overflow: "hidden",

    textOverflow: "ellipsis",
  },

  productName: {
    fontSize: "14px",

    margin: "0 0 9px",

    color: "#4B2954",

    fontFamily:
      '"Playfair Display", serif',

    fontWeight: "600",

    lineHeight: "1.3",

    whiteSpace: "nowrap",

    overflow: "hidden",

    textOverflow: "ellipsis",
  },

  priceRow: {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    flexWrap: "wrap",
  },

  currentPrice: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#C22730",
  },

  oldPrice: {
    fontSize: "11px",
    color: "#9E8C9E",
    textDecoration:
      "line-through",
  },

  // ==========================================================
  // MOBILE ACTIONS
  // ==========================================================

  mobileActions: {
    display: "flex",
    gap: "6px",
    padding: "0 8px 10px",
  },

  mobileQuickView: {
    flex: 1,

    padding: "7px 3px",

    border:
      "1px solid rgba(75, 41, 84, 0.15)",

    borderRadius: "8px",

    backgroundColor: "#FAF5FC",

    color: "#4B2954",

    cursor: "pointer",

    fontSize: "10px",

    fontWeight: "600",

    textTransform: "uppercase",

    whiteSpace: "nowrap",

    touchAction: "manipulation",
  },

  mobileAddToCart: {
    flex: 1,

    padding: "7px 3px",

    border: "none",

    borderRadius: "8px",

    background: "linear-gradient(135deg, #e2bc53 0%, #c99e32 100%)",

    color: "#222",

    cursor: "pointer",

    fontSize: "10px",

    fontWeight: "700",

    textTransform: "uppercase",

    whiteSpace: "nowrap",

    touchAction: "manipulation",
  },
};

export default VanyaNewArrivals;
